import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';
import { riskMeta, type Guide } from '@/lib/guideData';

type Props = {
  guide: Guide;
  layout?: 'grid' | 'row';
};

export default function GuideCard({ guide, layout = 'grid' }: Props) {
  const category = categories.find((c) => c.id === guide.category);
  const risk = riskMeta[guide.risk];

  if (layout === 'row') {
    return (
      <Link
        to={`/guides/${guide.slug}`}
        className="group flex flex-col sm:flex-row gap-4 md:gap-6 py-6 border-b border-background-200 hover:bg-background-100/60 transition-colors px-2 rounded-md"
      >
        <div className="w-full sm:w-44 h-32 sm:h-28 rounded-lg overflow-hidden shrink-0 bg-background-200">
          <img
            src={guide.cover}
            alt={`${guide.title} 避雷清单配图`}
            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
          />
        </div>
        <div className="flex-1 flex flex-col">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-label ${risk.className}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${risk.dot}`}></span>
              {risk.label}
            </span>
            <span className="text-xs text-foreground-500 font-label">{category?.name}</span>
          </div>
          <h3 className="font-heading text-lg font-bold text-foreground-950 group-hover:text-primary-700 transition-colors leading-snug">
            {guide.title}
          </h3>
          <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-2 font-body">{guide.summary}</p>
          <div className="mt-3 flex items-center gap-4 text-xs text-foreground-500 font-label">
            <span className="flex items-center gap-1"><i className="ri-time-line"></i>{guide.readTime}</span>
            <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{guide.views}</span>
            <span>{guide.date}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/guides/${guide.slug}`}
      className="group flex flex-col rounded-lg border border-background-200 bg-background-50 overflow-hidden hover:border-primary-300 transition-colors"
    >
      <div className="w-full h-44 overflow-hidden bg-background-200">
        <img
          src={guide.cover}
          alt={`${guide.title} 避雷清单配图`}
          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
        />
      </div>
      <div className="flex flex-col flex-1 p-4 md:p-5">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-label ${risk.className}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${risk.dot}`}></span>
            {risk.label}
          </span>
          <span className="text-xs text-foreground-500 font-label">{category?.name}</span>
        </div>
        <h3 className="font-heading text-base font-bold text-foreground-950 group-hover:text-primary-700 transition-colors leading-snug line-clamp-2">
          {guide.title}
        </h3>
        <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3 font-body flex-1">{guide.summary}</p>
        <div className="mt-4 pt-3 border-t border-background-200 flex items-center justify-between text-xs text-foreground-500 font-label">
          <span className="flex items-center gap-1"><i className="ri-time-line"></i>{guide.readTime}</span>
          <span className="flex items-center gap-1 text-primary-700">
            查看清单
            <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </Link>
  );
}